import React, { useEffect, useState } from 'react';
import { base44 } from '@/api/base44Client';
import TopBar from '@/components/atelier/TopBar';
import Nav from '@/components/atelier/Nav';
import Footer from '@/components/atelier/Footer';
import AtelierMap from '@/components/atelier/AtelierMap';
import Button from '@/components/atelier/Button';
import EyebrowPill from '@/components/atelier/EyebrowPill';
import SectionHeader from '@/components/atelier/SectionHeader';
import AreaPills from '@/components/atelier/AreaPills';
import FinalCTACard from '@/components/atelier/FinalCTACard';
import { addJsonLd, clearJsonLd, getSiteSchemas } from '@/lib/seo';

const BOOKING_URL = 'https://www.fresha.com/a/ikonomakis-hair-atelier-halandri-irakleitoy-80-bxynmijf?pId=770996';
const PHONE = '211 800 4132';
const PHONE_HREF = 'tel:+302118004132';
const EMAIL = 'info@ikonomakis.gr';
const ADDRESS = 'Ηρακλείτου 80, Πάτημα Χαλανδρίου, 152 38';

const AREAS = [
  'Αθήνα', 'Χαλάνδρι', 'Βόρεια Προάστια', 'Θεσσαλονίκη', 'Πάτρα',
  'Κρήτη', 'Κυκλάδες', 'Πελοπόννησος', 'Ήπειρος',
  'Φιλοθέη', 'Ψυχικό', 'Κηφισιά',
];

const HOURS = [
  { day: 'Δευτέρα', hours: 'Κλειστά' },
  { day: 'Τρίτη', hours: '10:00 – 20:00' },
  { day: 'Τετάρτη', hours: '09:00 – 17:00' },
  { day: 'Πέμπτη', hours: '10:00 – 20:00' },
  { day: 'Παρασκευή', hours: '10:00 – 20:00' },
  { day: 'Σάββατο', hours: '09:00 – 17:00' },
  { day: 'Κυριακή', hours: 'Κλειστά' },
];

function ContactForm() {
  const [submitted, setSubmitted] = useState(false);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const name = fd.get('name');
    const email = fd.get('email');
    const phone = fd.get('phone');
    const service = fd.get('service');
    const message = fd.get('message');
    
    const subject = encodeURIComponent(`Νέο μήνυμα από ${name}${service ? ` · ${service}` : ''}`);
    const body = encodeURIComponent(
      `Όνομα: ${name}\nEmail: ${email}\nΤηλέφωνο: ${phone}\nΥπηρεσία: ${service || '—'}\n\n${message}`
    );

    // "ψηφιοποιήσεις": a visitor turning into a recorded lead. generate_lead is
    // GA4's recommended standard event name for exactly this. Also mirrored to
    // Base44's own analytics for parity with the rest of the site's tracking.
    base44.analytics.track({ eventName: 'contact_form_submitted', properties: { service: service || null } });
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'generate_lead', {
        page_path: window.location.pathname,
        service: service || '(not specified)',
      });
    }

    window.location.href = `mailto:${EMAIL}?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };
  
  return (
    <div className="bg-ivory rounded-card-l p-8 lg:p-10">
      <h3 className="font-serif text-[28px] text-warm-black mb-2 font-medium leading-tight tracking-tight">
        Στείλε μας μήνυμα
      </h3>
      <p className="text-body-s text-muted mb-7 leading-relaxed">
        Συμπλήρωσε τη φόρμα και θα σου απαντήσουμε εντός 24 ωρών.
      </p>
      
      {submitted ? (
        <div className="bg-cream-soft border border-gold/30 rounded-card p-6 text-center">
          <div className="text-gold text-[32px] mb-2">✦</div>
          <div className="font-serif text-[20px] text-warm-black mb-2 font-medium">Ευχαριστούμε!</div>
          <p className="text-body-s text-muted">
            Το email σου άνοιξε. Στείλε το για να σου απαντήσουμε σύντομα.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-tiny uppercase tracking-wider text-muted mb-2 font-medium">
              Όνομα *
            </label>
            <input
              required
              type="text"
              name="name"
              className="w-full px-5 py-3 rounded-card-sm border border-brown/15 bg-cream-bg text-warm-black text-body-s focus:outline-none focus:border-gold transition-colors"
              placeholder="Το ονοματεπώνυμό σου"
            />
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-tiny uppercase tracking-wider text-muted mb-2 font-medium">
                Email *
              </label>
              <input
                required
                type="email"
                name="email"
                className="w-full px-5 py-3 rounded-card-sm border border-brown/15 bg-cream-bg text-warm-black text-body-s focus:outline-none focus:border-gold transition-colors"
                placeholder="email@example.com"
              />
            </div>
            <div>
              <label className="block text-tiny uppercase tracking-wider text-muted mb-2 font-medium">
                Τηλέφωνο
              </label>
              <input
                type="tel"
                name="phone"
                className="w-full px-5 py-3 rounded-card-sm border border-brown/15 bg-cream-bg text-warm-black text-body-s focus:outline-none focus:border-gold transition-colors"
                placeholder="69x xxx xxxx"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-tiny uppercase tracking-wider text-muted mb-2 font-medium">
              Υπηρεσία (προαιρετικά)
            </label>
            <select
              name="service"
              className="w-full px-5 py-3 rounded-card-sm border border-brown/15 bg-cream-bg text-warm-black text-body-s focus:outline-none focus:border-gold transition-colors"
            >
              <option value="">— Επίλεξε —</option>
              <option value="Balayage">Balayage</option>
              <option value="Hair Color">Hair Color</option>
              <option value="Color Correction">Color Correction</option>
              <option value="Keratin Treatment">Keratin Treatment</option>
              <option value="Hair Treatment">Hair Treatment</option>
              <option value="Κούρεμα">Κούρεμα</option>
              <option value="Face Metrics Pro">Face Metrics Pro</option>
              <option value="Άλλο">Άλλο</option>
            </select>
          </div>
          
          <div>
            <label className="block text-tiny uppercase tracking-wider text-muted mb-2 font-medium">
              Μήνυμα *
            </label>
            <textarea
              required
              name="message"
              rows={5}
              className="w-full px-5 py-3 rounded-card-sm border border-brown/15 bg-cream-bg text-warm-black text-body-s focus:outline-none focus:border-gold transition-colors resize-none"
              placeholder="Πες μας τι έχεις στο μυαλό σου..."
            />
          </div>
          
          <Button variant="primary" className="w-full mt-2">
            Αποστολή Μηνύματος →
          </Button>
        </form>
      )}
    </div>
  );
}

const CONTACT_FAQS = [
  { q: 'Πού βρίσκεστε;', a: 'Ηρακλείτου 80, Πάτημα Χαλανδρίου, 152 38 Αθήνα. Άνετο παρκάρισμα στο δρόμο, κοντά σε στάσεις λεωφορείου και μετρό.' },
  { q: 'Πώς μπορώ να κλείσω ραντεβού;', a: 'Online μέσω Fresha σε 60 δευτερόλεπτα — επιλογή υπηρεσίας και stylist tier. Εναλλακτικά τηλεφωνικά στο 211 800 4132.' },
  { q: 'Τι ώρες είστε ανοιχτά;', a: 'Τρίτη, Πέμπτη & Παρασκευή 10:00–20:00. Τετάρτη & Σάββατο 09:00–17:00. Δευτέρα και Κυριακή κλειστά.' },
  { q: 'Χρειάζομαι consultation πριν κλείσω ραντεβού;', a: 'Για μεγάλες μεταβάσεις χρώματος ή color correction συνιστάται consultation 30-45 λεπτών. Για τακτικές υπηρεσίες δεν απαιτείται.' },
  { q: 'Έχετε parking;', a: 'Ναι, άνετο παρκάρισμα στον δρόμο και κοντινά parking στο Πάτημα Χαλανδρίου.' },
];

export default function ContactPage() {
  useEffect(() => {
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: CONTACT_FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    clearJsonLd();
    getSiteSchemas().forEach(addJsonLd);
    addJsonLd(faqSchema);
  }, []);

  return (
    <div className="bg-cream-bg text-warm-black">
      <TopBar />
      <Nav />
      
      {/* HERO */}
      <section className="px-[6vw] pt-[80px] pb-[60px]">
        <div className="max-w-editorial mx-auto text-center">
          <EyebrowPill variant="dark" className="mb-7">
            Επικοινωνία
          </EyebrowPill>
          <h1 className="font-serif text-display-l text-warm-black font-medium tracking-[-2px] leading-[0.95] mb-7">
            Σε περιμένουμε<br />
            <em className="italic text-gold">στο atelier μας.</em>
          </h1>
          <p className="text-body-l text-muted leading-[1.65] max-w-[600px] mx-auto">
            Κάλεσέ μας, στείλε email, ή κλείσε ραντεβού online. Όποιον τρόπο επικοινωνίας προτιμάς — είμαστε εδώ.
          </p>
        </div>
      </section>
      
      {/* QUICK CONTACT */}
      <section className="px-[6vw] pb-[60px]">
        <div className="max-w-container mx-auto grid grid-cols-1 md:grid-cols-3 gap-5">
          <a 
            href={PHONE_HREF}
            className="bg-ivory rounded-card-l p-7 hover:shadow-card hover:-translate-y-1 transition-all duration-400 text-center group"
          >
            <div className="text-gold text-[32px] mb-3 group-hover:scale-110 transition-transform duration-300">📞</div>
            <div className="text-eyebrow uppercase tracking-wider text-muted mb-2 font-medium">Τηλέφωνο</div>
            <div className="font-serif text-[22px] text-warm-black font-medium">
              {PHONE}
            </div>
          </a>
          
          <a 
            href={`mailto:${EMAIL}`}
            className="bg-ivory rounded-card-l p-7 hover:shadow-card hover:-translate-y-1 transition-all duration-400 text-center group"
          >
            <div className="text-gold text-[32px] mb-3 group-hover:scale-110 transition-transform duration-300">✉</div>
            <div className="text-eyebrow uppercase tracking-wider text-muted mb-2 font-medium">Email</div>
            <div className="font-serif text-[18px] text-warm-black font-medium">
              {EMAIL}
            </div>
          </a>
          
          <a 
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-warm-black text-cream rounded-card-l p-7 hover:shadow-card hover:-translate-y-1 transition-all duration-400 text-center group"
          >
            <div className="text-gold text-[32px] mb-3 group-hover:scale-110 transition-transform duration-300">✦</div>
            <div className="text-eyebrow uppercase tracking-wider text-gold-light mb-2 font-medium">Online Booking</div>
            <div className="font-serif text-[22px] text-cream font-medium">
              Fresha →
            </div>
          </a>
        </div>
      </section>
      
      {/* MAIN: form + map */}
      <section className="px-[6vw] py-[80px]">
        <div className="max-w-container-narrow mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-10 lg:gap-12">
          <ContactForm />
          
          <div className="space-y-6">
            {/* Google Maps embed */}
            <AtelierMap />
            
            {/* Address card */}
            <div className="bg-ivory rounded-card-l p-7">
              <div className="text-eyebrow uppercase tracking-wider text-gold mb-3 font-medium">
                Διεύθυνση
              </div>
              <address className="not-italic font-serif text-[20px] text-warm-black leading-[1.4] mb-5 font-medium">
                Ηρακλείτου 80<br />
                Πάτημα Χαλανδρίου<br />
                152 38 Αθήνα
              </address>
              <a 
                href="https://maps.google.com/?q=Ηρακλείτου+80,+Πάτημα+Χαλανδρίου,+Αθήνα"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-body-s text-warm-black font-medium hover:text-gold transition-colors"
              >
                Άνοιξε στο Google Maps →
              </a>
            </div>
          </div>
        </div>
      </section>
      
      {/* HOURS */}
      <section className="bg-warm-black text-cream px-[6vw] py-[100px]">
        <div className="max-w-container-narrow mx-auto">
          <SectionHeader
            variant="light"
            eyebrow="Ωράριο Λειτουργίας"
            title="Πότε μπορείς"
            titleEm="να μας επισκεφθείς."
            center
          />
          
          <div className="max-w-[600px] mx-auto bg-ivory/5 border border-gold-light/15 rounded-card-l p-8">
            {HOURS.map((h, i) => (
              <div 
                key={i}
                className={`flex justify-between items-center py-3 ${i < HOURS.length - 1 ? 'border-b border-gold-light/10' : ''}`}
              >
                <span className="font-serif text-[18px] text-cream font-medium">{h.day}</span>
                <span className={`text-body-s ${h.hours === 'Κλειστά' ? 'text-cream/40' : 'text-gold-light'}`}>
                  {h.hours}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* AREAS */}
      <section className="px-[6vw] py-[100px]">
        <div className="max-w-container-narrow mx-auto text-center">
          <SectionHeader
            eyebrow="Περιοχές που εξυπηρετούμε"
            title="Στο Χαλάνδρι,"
            titleEm="για όλη την Αθήνα."
            subtitle="Η φυσική μας έδρα είναι στο Χαλάνδρι, όμως δεχόμαστε πελάτες από την Αθήνα και όλη την Ελλάδα για consultation, Face Metrics Pro, χρώμα, κούρεμα και premium θεραπείες."
            center
          />
          <AreaPills variant="light" areas={AREAS} className="justify-center" />
        </div>
      </section>
      
      {/* FAQ — AEO: structured Q&As for Answer Engines */}
      <section className="px-[6vw] py-[100px] bg-cream-soft">
        <div className="max-w-editorial mx-auto">
          <h2 className="font-serif text-display-s text-warm-black font-medium tracking-[-1.5px] leading-[1] mb-12 text-center">
            Συχνές <em className="italic text-gold">ερωτήσεις</em>
          </h2>
          <div className="grid gap-8">
            {[
              { q: 'Πού βρίσκεστε;', a: 'Ηρακλείτου 80, Πάτημα Χαλανδρίου, 152 38 Αθήνα. Άνετο παρκάρισμα στο δρόμο, κοντά σε στάσεις λεωφορείου και μετρό.' },
              { q: 'Πώς μπορώ να κλείσω ραντεβού;', a: 'Online μέσω Fresha σε 60 δευτερόλεπτα — επιλογή υπηρεσίας και stylist tier. Εναλλακτικά τηλεφωνικά στο 211 800 4132.' },
              { q: 'Τι ώρες είστε ανοιχτά;', a: 'Τρίτη, Πέμπτη & Παρασκευή 10:00–20:00. Τετάρτη & Σάββατο 09:00–17:00. Δευτέρα και Κυριακή κλειστά.' },
              { q: 'Χρειάζομαι consultation πριν κλείσω ραντεβού;', a: 'Για μεγάλες μεταβάσεις χρώματος ή color correction συνιστάται consultation 30-45 λεπτών. Για τακτικές υπηρεσίες (κούρεμα, ανανέωση χρώματος) δεν απαιτείται.' },
              { q: 'Έχετε parking;', a: 'Ναι, άνετο παρκάρισμα στον δρόμο και κοντινά parking στο Πάτημα Χαλανδρίου.' },
            ].map((faq) => (
              <div key={faq.q} className="border-b border-brown/10 pb-6">
                <h3 className="aeo-question font-serif text-[20px] text-warm-black font-medium mb-3">{faq.q}</h3>
                <p className="aeo-answer text-body text-muted leading-[1.7]">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-[6vw] py-[120px]">
        <FinalCTACard
          title="Έτοιμη να"
          titleEm="μας επισκεφθείς;"
          description="Κλείσε ραντεβού online σε 60 δευτερόλεπτα — επιλογή 3 tiers, ευέλικτες ώρες, secure booking."
          ctaText="Κράτηση Online →"
          ctaHref={BOOKING_URL}
          ctaTarget="_blank"
          ctaRel="noopener noreferrer"
        />
      </section>
      
      <Footer />
    </div>
  );
}